import { Filter } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "~/components/ui";
import { type DipColumnFilterValues, EMPTY_DIP_COLUMN_FILTER_VALUES } from "~/features/dataImportProjects/types";
import { cn } from "~/lib/utils";

const ALL_STATUSES = "__all";

// The list hides deactivated projects by default; the toggle is only "active" when flipped off.
const DEFAULT_EXCLUDE_DEACTIVATED = true;

interface DataImportProjectColumnFiltersProps {
  value: DipColumnFilterValues;
  statusOptions: string[];
  excludeDeactivated: boolean;
  // One call: two setParams would clobber (see onApplyFilters in the container).
  onApply: (next: DipColumnFilterValues, excludeDeactivated: boolean) => void;
}

function hasActiveFilter(value: DipColumnFilterValues): boolean {
  return Object.values(value).some((v) => v !== "");
}

export function DataImportProjectColumnFilters({
  value,
  statusOptions,
  excludeDeactivated,
  onApply,
}: DataImportProjectColumnFiltersProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [draftExcludeDeactivated, setDraftExcludeDeactivated] = useState(excludeDeactivated);

  const active = hasActiveFilter(value) || excludeDeactivated !== DEFAULT_EXCLUDE_DEACTIVATED;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setDraft(value);
          setDraftExcludeDeactivated(excludeDeactivated);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={t("ui.filter")}
          className={cn(active && "border-primary-500 text-primary-700")}
        >
          <Filter className={cn(active && "fill-primary-100")} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 flex items-center justify-between">
            <Label htmlFor="dip-filter-exclude-deactivated">{t("ui.excludeDeactivated")}</Label>
            <Switch
              id="dip-filter-exclude-deactivated"
              checked={draftExcludeDeactivated}
              onCheckedChange={setDraftExcludeDeactivated}
              aria-label={t("ui.excludeDeactivated")}
            />
          </div>
          <div className="col-span-2 space-y-1">
            <Label htmlFor="dip-filter-seq">{t("model.dataImportProject.seqID")}</Label>
            <Input
              id="dip-filter-seq"
              value={draft.seq}
              onChange={(e) => setDraft((prev) => ({ ...prev, seq: e.target.value }))}
            />
          </div>
          <div className="col-span-2 space-y-1">
            <Label htmlFor="dip-filter-name">{t("model.common.name")}</Label>
            <Input
              id="dip-filter-name"
              value={draft.name}
              onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>
          <div className="col-span-2 space-y-1">
            <Label htmlFor="dip-filter-status">{t("model.dataImportProject.status")}</Label>
            <Select
              value={draft.status || ALL_STATUSES}
              onValueChange={(next) => setDraft((prev) => ({ ...prev, status: next === ALL_STATUSES ? "" : next }))}
            >
              <SelectTrigger id="dip-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATUSES}>{t("ui.allStatuses")}</SelectItem>
                {statusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1">
            <Label htmlFor="dip-filter-references">{t("model.reference.modelNames")}</Label>
            <Input
              id="dip-filter-references"
              value={draft.references}
              onChange={(e) => setDraft((prev) => ({ ...prev, references: e.target.value }))}
            />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>{t("model.dataImportProject.startDate")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                aria-label={t("ui.from")}
                value={draft.startAtFrom}
                onChange={(e) => setDraft((prev) => ({ ...prev, startAtFrom: e.target.value }))}
              />
              <Input
                type="date"
                aria-label={t("ui.to")}
                value={draft.startAtTo}
                onChange={(e) => setDraft((prev) => ({ ...prev, startAtTo: e.target.value }))}
              />
            </div>
          </div>
          <div className="col-span-2 space-y-1">
            <Label>{t("model.dataImportProject.endDate")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                aria-label={t("ui.from")}
                value={draft.endAtFrom}
                onChange={(e) => setDraft((prev) => ({ ...prev, endAtFrom: e.target.value }))}
              />
              <Input
                type="date"
                aria-label={t("ui.to")}
                value={draft.endAtTo}
                onChange={(e) => setDraft((prev) => ({ ...prev, endAtTo: e.target.value }))}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border-default pt-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setDraft(EMPTY_DIP_COLUMN_FILTER_VALUES);
              setDraftExcludeDeactivated(DEFAULT_EXCLUDE_DEACTIVATED);
              onApply(EMPTY_DIP_COLUMN_FILTER_VALUES, DEFAULT_EXCLUDE_DEACTIVATED);
              setOpen(false);
            }}
          >
            {t("ui.clearAll")}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              onApply(draft, draftExcludeDeactivated);
              setOpen(false);
            }}
          >
            {t("ui.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
